import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { API_BASE } from '@/config';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/contexts/LocaleContext';
import { toBcp47Locale } from '@/locales/locale';
import { useToast } from '../contexts/ToastContext';
import { useCreditCheck } from '../hooks/useCreditCheck';
import {
  buildAsOfTimelineSelectOptions,
  type AsOfTimelineItem,
} from '../utils/character-chat-as-of-options';
import {
  parseAsOfDateCutoffIso,
  resolveAsOfDateCutoffIso,
} from '../utils/character-chat-as-of-date';
import {
  CHARACTER_CHAT_NARRATOR_VALUE,
  sortCharacterChatPeersByRelevance,
  type CharacterChatPeer,
} from '../utils/character-chat-speakers';
import { sortThreadsOldestFirst } from '../utils/chat-threads';
import { Button } from './ui/button';
import {
  ButtonTooltip,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { DatePicker } from './DatePicker';
import { Input } from './ui/input';
import { UserAvatar } from './UserAvatar';
import { CharacterAvatar } from './CharacterAvatar';
import { ScribaneAvatar } from './ScribaneAvatar';
import { ChatMarkdownContent } from './ChatMarkdownContent';
import {
  CHAT_ACTIVE_THREAD_CLASSNAME,
  CHAT_USER_MESSAGE_BUBBLE_CLASSNAME,
} from './chat-ui-classes';
import { ENTITY_DETAIL_BODY_SCROLL_ATTR } from './EntityDetailModalLayout';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { SingleSelectCombobox } from './ui/single-select-combobox';
import { cn } from '../lib/utils';

export type { CharacterChatPeer };

type CharacterChatMode = 'unrestricted' | 'as_of' | 'as_of_date';

const MODE_TOOLTIP_ATTR = 'data-character-chat-mode-tooltip';

const AutoTimelineRequiredHint: React.FC<{
  projectId?: string | null;
  timelineLabel: string;
  template: string;
}> = ({ projectId, timelineLabel, template }) => {
  const [before, after] = template.split('{{link}}');
  const timelineHref = projectId ? `/projects/${projectId}/timeline` : null;

  return (
    <span className="inline">
      {before}
      {timelineHref ? (
        <Link
          to={timelineHref}
          className="font-medium text-primary underline underline-offset-2 hover:text-primary/80"
          onClick={(event) => event.stopPropagation()}
        >
          {timelineLabel}
        </Link>
      ) : (
        timelineLabel
      )}
      {after ?? ''}
    </span>
  );
};

const ModeSelectItem: React.FC<{
  value: CharacterChatMode;
  disabled?: boolean;
  disabledTooltip?: React.ReactNode;
  children: React.ReactNode;
}> = ({ value, disabled, disabledTooltip, children }) => {
  const item = (
    <SelectItem
      value={value}
      disabled={disabled}
      className={disabled ? 'data-[disabled]:pointer-events-auto data-[disabled]:cursor-not-allowed' : undefined}
    >
      {children}
    </SelectItem>
  );

  if (!disabled || !disabledTooltip) {
    return item;
  }

  return (
    <Tooltip disableHoverableContent={false}>
      <TooltipTrigger asChild>{item}</TooltipTrigger>
      <TooltipContent
        side="right"
        className="z-[2020] max-w-xs border border-border bg-popover text-popover-foreground shadow-md"
        {...{ [MODE_TOOLTIP_ATTR]: '' }}
      >
        {disabledTooltip}
      </TooltipContent>
    </Tooltip>
  );
};

type ChatSpeaker = 'character' | 'scribane';

type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
  metadata?: { speaker?: ChatSpeaker; timelineAutoStale?: boolean } | null;
};

type ChatThread = {
  id: string;
  title: string | null;
  asOfTimelineItemId: string | null;
  asOfDate?: string | null;
  speakerAsCharacterId?: string | null;
  createdAt: string;
};

type TimelineOption = AsOfTimelineItem & {
  item_type?: string;
  itemType?: string;
};

type FreshnessDoc = {
  documentId: string;
  drift: string;
  freshnessPercent: number | null;
};

type CharacterChatProps = {
  documentId: string;
  characterId: string;
  characterName: string;
  projectId?: string | null;
  chromeContainer?: HTMLElement | null;
  otherCharacters?: CharacterChatPeer[];
};

export const CharacterChat: React.FC<CharacterChatProps> = ({
  documentId,
  characterId,
  characterName,
  projectId,
  chromeContainer,
  otherCharacters = [],
}) => {
  const { token, user } = useAuth();
  const { t, locale } = useLocale();
  const dateLocale = toBcp47Locale(locale);
  const { showErrorToast } = useToast();
  const { checkCredits, CreditLimitModalComponent } = useCreditCheck();
  const [mode, setMode] = useState<CharacterChatMode>('unrestricted');
  const [asOfId, setAsOfId] = useState<string>('');
  const [asOfDateYmd, setAsOfDateYmd] = useState('');
  const [asOfTimeHm, setAsOfTimeHm] = useState('');
  const [speakerAsCharacterId, setSpeakerAsCharacterId] = useState<string | null>(null);
  const [timelineItems, setTimelineItems] = useState<TimelineOption[]>([]);
  const [autoFreshness, setAutoFreshness] = useState<{
    totalAutoCount: number;
    hasModerateOrMajorDrift: boolean;
    documents: FreshnessDoc[];
  } | null>(null);
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [question, setQuestion] = useState('');
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [currentSpeaker, setCurrentSpeaker] = useState<ChatSpeaker>('character');
  const [loading, setLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const threadListEndRef = useRef<HTMLDivElement>(null);

  const speakerPeers = useMemo(
    () =>
      sortCharacterChatPeersByRelevance(
        otherCharacters.filter((peer) => peer.id !== characterId)
      ),
    [otherCharacters, characterId]
  );
  const speakingAsPeer = useMemo(
    () => speakerPeers.find((peer) => peer.id === speakerAsCharacterId) ?? null,
    [speakerPeers, speakerAsCharacterId]
  );
  const speakerSelectOptions = useMemo(
    () => [
      {
        value: CHARACTER_CHAT_NARRATOR_VALUE,
        label: t('character_chat_speaker_narrator'),
      },
      ...speakerPeers.map((peer) => ({
        value: peer.id,
        label: peer.name,
      })),
    ],
    [speakerPeers, t]
  );

  const knowledgeTypes = new Set([
    'historical_event',
    'fictional_event',
    'auto_historical',
    'auto_fictional',
  ]);

  const loadThreads = useCallback(async () => {
    if (!token) return;
    const response = await fetch(
      `${API_BASE}/api/character-chat/threads/${documentId}/${characterId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!response.ok) return;
    const data = await response.json();
    setThreads(sortThreadsOldestFirst(data.threads || []));
  }, [token, documentId, characterId]);

  const loadTimeline = useCallback(async () => {
    if (!token || !projectId) return;
    const [itemsRes, freshRes] = await Promise.all([
      fetch(`${API_BASE}/api/projects/${projectId}/timeline-items`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
      fetch(`${API_BASE}/api/projects/${projectId}/timeline-auto-freshness`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    ]);
    if (itemsRes.ok) {
      const data = await itemsRes.json();
      const items = (data.items || data.timelineItems || []) as TimelineOption[];
      setTimelineItems(
        items.filter((item) => knowledgeTypes.has(String(item.item_type || item.itemType || '')))
      );
    }
    if (freshRes.ok) {
      const data = await freshRes.json();
      setAutoFreshness(data.freshness || null);
    }
  }, [token, projectId]);

  useEffect(() => {
    void loadThreads();
    void loadTimeline();
  }, [loadThreads, loadTimeline]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [messages, currentAnswer]);

  useEffect(() => {
    if (!historyOpen) return;
    threadListEndRef.current?.scrollIntoView?.({ behavior: 'auto' });
  }, [historyOpen, threads]);

  const applyThreadMode = useCallback((thread: ChatThread): void => {
    setSpeakerAsCharacterId(thread.speakerAsCharacterId ?? null);
    if (thread.asOfTimelineItemId) {
      setMode('as_of');
      setAsOfId(thread.asOfTimelineItemId);
      return;
    }
    if (thread.asOfDate) {
      try {
        const parsed = parseAsOfDateCutoffIso(
          typeof thread.asOfDate === 'string' ? thread.asOfDate : String(thread.asOfDate)
        );
        setMode('as_of_date');
        setAsOfDateYmd(parsed.dateYmd);
        setAsOfTimeHm(parsed.timeHm);
      } catch {
        setMode('as_of_date');
      }
      return;
    }
    setMode('unrestricted');
  }, []);

  const loadMessages = async (threadId: string): Promise<void> => {
    if (!token) return;
    const response = await fetch(`${API_BASE}/api/chat/threads/${threadId}/messages`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) return;
    const data = await response.json();
    const thread = threads.find((entry) => entry.id === threadId);
    if (thread) {
      applyThreadMode(thread);
    }
    setMessages(data.messages || []);
    setActiveThreadId(threadId);
  };

  const chatStarted = messages.length > 0 || Boolean(activeThreadId);
  const isStrictMode = mode === 'as_of' || mode === 'as_of_date';
  const hasAutoTimeline = (autoFreshness?.totalAutoCount ?? 0) > 0;
  const strictModesDisabled = !hasAutoTimeline;
  const strictWarned = isStrictMode && Boolean(autoFreshness?.hasModerateOrMajorDrift);
  const asOfOptions = useMemo(
    () => buildAsOfTimelineSelectOptions(timelineItems, dateLocale),
    [timelineItems, dateLocale]
  );
  const lockedModeLabel = useMemo(() => {
    if (mode === 'as_of') {
      const eventLabel = asOfOptions.find((option) => option.value === asOfId)?.label ?? asOfId;
      return eventLabel
        ? `${t('character_chat_mode_as_of')}: ${eventLabel}`
        : t('character_chat_mode_as_of');
    }
    if (mode === 'as_of_date') {
      const detail = asOfTimeHm ? `${asOfDateYmd} ${asOfTimeHm}` : asOfDateYmd;
      return detail
        ? `${t('character_chat_mode_as_of_date')}: ${detail}`
        : t('character_chat_mode_as_of_date');
    }
    return t('character_chat_mode_unrestricted');
  }, [asOfDateYmd, asOfId, asOfOptions, asOfTimeHm, mode, t]);
  const lockedSpeakerLabel = speakerAsCharacterId
    ? speakingAsPeer?.name ?? speakerAsCharacterId
    : t('character_chat_speaker_narrator');
  const sendAsLabel = speakingAsPeer
    ? t('character_chat_send_as').replace('{name}', speakingAsPeer.name)
    : t('character_chat_send_as_narrator');
  const autoTimelineRequiredHint = (
    <AutoTimelineRequiredHint
      projectId={projectId}
      timelineLabel={t('Timeline')}
      template={t('character_chat_auto_timeline_required_tooltip')}
    />
  );
  const keepSelectOpenForModeTooltip = (event: Event): void => {
    const target = event.target;
    if (target instanceof Element && target.closest(`[${MODE_TOOLTIP_ATTR}]`)) {
      event.preventDefault();
    }
  };

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (!token || !question.trim() || loading) return;
    if (isStrictMode && strictModesDisabled) {
      showErrorToast(t('character_chat_auto_timeline_required'));
      return;
    }
    if (mode === 'as_of' && !asOfId) {
      showErrorToast(t('character_chat_as_of_required'));
      return;
    }
    if (mode === 'as_of_date' && !asOfDateYmd) {
      showErrorToast(t('character_chat_as_of_date_required'));
      return;
    }
    if (!(await checkCredits())) return;

    let asOfDateIso: string | null = null;
    if (mode === 'as_of_date') {
      try {
        asOfDateIso = resolveAsOfDateCutoffIso(asOfDateYmd, asOfTimeHm || null);
      } catch {
        showErrorToast(t('character_chat_as_of_date_required'));
        return;
      }
    }

    setLoading(true);
    const questionToSend = question.trim();
    setQuestion('');
    const tempId = `temp-user-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      {
        id: tempId,
        role: 'user',
        content: questionToSend,
        createdAt: new Date().toISOString(),
      },
    ]);

    try {
      const body: Record<string, unknown> = {
        documentId,
        characterId,
        question: questionToSend,
        mode,
      };
      if (activeThreadId) {
        body.threadId = activeThreadId;
      } else {
        body.speakerAsCharacterId = speakerAsCharacterId;
      }
      if (mode === 'as_of') {
        body.asOfTimelineItemId = asOfId;
      }
      if (mode === 'as_of_date' && asOfDateIso) {
        body.asOfDate = asOfDateIso;
      }

      const response = await fetch(`${API_BASE}/api/character-chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed');
      }
      if (!response.body) throw new Error('No body');

      const newThreadId = response.headers.get('X-Chat-Thread-Id');
      const speakerHeader = response.headers.get('X-Chat-Speaker') as ChatSpeaker | null;
      setCurrentSpeaker(speakerHeader === 'scribane' ? 'scribane' : 'character');

      if (newThreadId && !activeThreadId) {
        setActiveThreadId(newThreadId);
        setThreads((prev) =>
          sortThreadsOldestFirst([
            ...prev,
            {
              id: newThreadId,
              title: questionToSend.slice(0, 200),
              asOfTimelineItemId: mode === 'as_of' ? asOfId : null,
              asOfDate: mode === 'as_of_date' ? asOfDateIso : null,
              speakerAsCharacterId,
              createdAt: new Date().toISOString(),
            },
          ])
        );
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let full = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        full += decoder.decode(value, { stream: true });
        setCurrentAnswer(full);
      }

      setMessages((prev) => [
        ...prev,
        {
          id: `temp-assistant-${Date.now()}`,
          role: 'assistant',
          content: full,
          createdAt: new Date().toISOString(),
          metadata: { speaker: speakerHeader === 'scribane' ? 'scribane' : 'character' },
        },
      ]);
      setCurrentAnswer('');
    } catch (error) {
      console.error(error);
      showErrorToast(t('character_chat_failed'));
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
    } finally {
      setLoading(false);
    }
  };

  const speakerLabel = (message: ChatMessage): string => {
    if (message.role === 'user') {
      return speakingAsPeer?.name ?? t('character_chat_speaker_narrator');
    }
    if (message.metadata?.speaker === 'scribane') return t('scribane');
    return characterName;
  };

  const renderAvatar = (label: string, avatar: React.ReactElement): React.ReactNode => (
    <ButtonTooltip tooltip={label} side="top">
      <span className="inline-flex shrink-0" aria-label={label}>
        {avatar}
      </span>
    </ButtonTooltip>
  );

  const messageAvatar = (message: ChatMessage): React.ReactNode => {
    if (message.role === 'user') {
      if (speakingAsPeer) {
        return renderAvatar(
          speakerLabel(message),
          <CharacterAvatar name={speakingAsPeer.name} />
        );
      }
      return renderAvatar(speakerLabel(message), <UserAvatar email={user?.email} />);
    }
    if (message.metadata?.speaker === 'scribane') {
      return renderAvatar(speakerLabel(message), <ScribaneAvatar />);
    }
    return renderAvatar(speakerLabel(message), <CharacterAvatar name={characterName} />);
  };

  const startNewChat = (): void => {
    setActiveThreadId(null);
    setMessages([]);
    setCurrentAnswer('');
    setSpeakerAsCharacterId(null);
  };

  const chrome = (
    <div className="flex flex-col gap-3">
      {!chatStarted ? (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <ButtonTooltip
              tooltip={
                strictModesDisabled
                  ? ({
                      children: autoTimelineRequiredHint,
                      className:
                        'z-[2020] max-w-xs border border-border bg-popover text-popover-foreground shadow-md',
                      ...({ [MODE_TOOLTIP_ATTR]: '' } as Record<string, string>),
                    } as React.ComponentProps<typeof TooltipContent>)
                  : t('character_chat_mode_tooltip')
              }
            >
              <span className="text-xs font-medium text-muted-foreground">
                {t('character_chat_mode_label')}
              </span>
            </ButtonTooltip>
            <Select
              value={mode}
              onValueChange={(value) => {
                setMode(value as CharacterChatMode);
                setActiveThreadId(null);
                setMessages([]);
              }}
            >
              <SelectTrigger className="w-[220px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent
                className="z-[2010]"
                onPointerDownOutside={keepSelectOpenForModeTooltip}
              >
                <TooltipProvider delayDuration={200}>
                  <SelectItem value="unrestricted">{t('character_chat_mode_unrestricted')}</SelectItem>
                  <ModeSelectItem
                    value="as_of"
                    disabled={strictModesDisabled}
                    disabledTooltip={autoTimelineRequiredHint}
                  >
                    {t('character_chat_mode_as_of')}
                  </ModeSelectItem>
                  <ModeSelectItem
                    value="as_of_date"
                    disabled={strictModesDisabled}
                    disabledTooltip={autoTimelineRequiredHint}
                  >
                    {t('character_chat_mode_as_of_date')}
                  </ModeSelectItem>
                </TooltipProvider>
              </SelectContent>
            </Select>
            {mode === 'as_of' ? (
              <SingleSelectCombobox
                options={asOfOptions}
                value={asOfId}
                onValueChange={setAsOfId}
                placeholder={t('character_chat_as_of_placeholder')}
                searchPlaceholder={t('timeline_filter_search_placeholder')}
                emptyMessage={t('timeline_filter_no_results')}
                inputClassName="min-w-0 w-[min(100%,16rem)] sm:w-64"
                contentClassName="z-[2010] w-72 max-w-[min(100vw-2rem,18rem)]"
              />
            ) : null}
            {mode === 'as_of_date' ? (
              <div className="flex flex-wrap items-center gap-2">
                <DatePicker
                  value={asOfDateYmd}
                  onChange={setAsOfDateYmd}
                  className="w-[min(100%,12rem)]"
                />
                <Input
                  type="time"
                  value={asOfTimeHm}
                  onChange={(e) => setAsOfTimeHm(e.target.value)}
                  className="w-[8.5rem]"
                  aria-label={t('character_chat_as_of_time_optional')}
                  title={t('character_chat_as_of_time_optional')}
                />
              </div>
            ) : null}
          </div>
          {speakerPeers.length > 0 ? (
            <div className="flex flex-wrap items-center gap-2">
              <ButtonTooltip tooltip={t('character_chat_choose_speaker_tooltip')}>
                <span className="text-xs font-medium text-muted-foreground">
                  {t('character_chat_speaker_label')}
                </span>
              </ButtonTooltip>
              <SingleSelectCombobox
                options={speakerSelectOptions}
                value={speakerAsCharacterId ?? CHARACTER_CHAT_NARRATOR_VALUE}
                onValueChange={(value) => {
                  setSpeakerAsCharacterId(
                    value === CHARACTER_CHAT_NARRATOR_VALUE ? null : value
                  );
                }}
                placeholder={t('character_chat_speaker_narrator')}
                searchPlaceholder={t('timeline_filter_search_placeholder')}
                emptyMessage={t('timeline_filter_no_results')}
                inputClassName="min-w-0 w-[min(100%,12rem)] sm:w-48"
                contentClassName="z-[2010] w-72 max-w-[min(100vw-2rem,18rem)]"
                disabled={loading}
              />
            </div>
          ) : null}
        </div>
      ) : null}

      <form onSubmit={(e) => void handleSubmit(e)} className="flex gap-2">
        <input
          className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder={t('character_chat_placeholder')}
          disabled={loading}
        />
        <ButtonTooltip tooltip={t('character_chat_send_tooltip')}>
          <Button type="submit" disabled={loading || !question.trim()}>
            {loading ? t('sending') || '…' : sendAsLabel}
          </Button>
        </ButtonTooltip>
      </form>
    </div>
  );

  const isEmbeddedInModal = chromeContainer !== undefined;

  const transcript = (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      <div
        {...{ [ENTITY_DETAIL_BODY_SCROLL_ATTR]: '' }}
        className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 sm:px-6"
        style={
          isEmbeddedInModal
            ? {
                paddingTop: 'calc(var(--entity-detail-header-height, 4rem) + 1rem)',
                paddingBottom: 'calc(var(--entity-detail-footer-height, 5rem) + 1rem)',
              }
            : { paddingTop: '1rem', paddingBottom: '1rem' }
        }
      >
        {!chatStarted ? (
          <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
            {mode === 'unrestricted'
              ? t('character_chat_unrestricted_banner')
              : mode === 'as_of_date'
                ? t('character_chat_as_of_date_banner')
                : t('character_chat_as_of_banner')}
          </div>
        ) : (
          <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
            <div>{t('character_chat_mode_locked').replace('{label}', lockedModeLabel)}</div>
            <div>
              {t('character_chat_speaker_locked').replace('{label}', lockedSpeakerLabel)}
            </div>
          </div>
        )}

        {strictWarned ? (
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm">
            {t('character_chat_timeline_stale_warning')}
            <ul className="mt-1 list-disc pl-4">
              {(autoFreshness?.documents || [])
                .filter((d) => d.drift === 'moderate' || d.drift === 'major' || d.drift === 'missing_sync_metadata')
                .map((d) => (
                  <li key={d.documentId}>
                    {d.documentId.slice(0, 8)}…{' '}
                    {d.freshnessPercent != null ? `~${d.freshnessPercent}%` : d.drift}
                  </li>
                ))}
            </ul>
          </div>
        ) : null}

        {messages.map((message) => (
          <div
            key={message.id}
            className={cn('flex gap-2', message.role === 'user' ? 'justify-end' : 'justify-start')}
          >
            {message.role !== 'user' ? messageAvatar(message) : null}
            <div
              className={cn(
                'max-w-[80%] rounded-md px-3 py-2 text-sm',
                message.role === 'user'
                  ? cn(CHAT_USER_MESSAGE_BUBBLE_CLASSNAME, 'whitespace-pre-wrap')
                  : message.metadata?.speaker === 'scribane'
                    ? 'bg-muted border border-border text-foreground'
                    : 'bg-secondary text-secondary-foreground'
              )}
            >
              {message.role === 'user' ? (
                message.content
              ) : (
                <ChatMarkdownContent content={message.content} />
              )}
            </div>
            {message.role === 'user' ? messageAvatar(message) : null}
          </div>
        ))}
        {currentAnswer ? (
          <div className="flex gap-2 justify-start">
            {currentSpeaker === 'scribane'
              ? renderAvatar(t('scribane'), <ScribaneAvatar />)
              : renderAvatar(characterName, <CharacterAvatar name={characterName} />)}
            <div
              className={cn(
                'max-w-[80%] rounded-md px-3 py-2 text-sm',
                currentSpeaker === 'scribane'
                  ? 'bg-muted border border-border text-foreground'
                  : 'bg-secondary text-secondary-foreground'
              )}
            >
              <ChatMarkdownContent content={currentAnswer} />
            </div>
          </div>
        ) : null}
        <div ref={bottomRef} />
      </div>
    </div>
  );

  const renderedChrome =
    chromeContainer !== undefined
      ? chromeContainer
        ? createPortal(chrome, chromeContainer)
        : null
      : chrome;

  const formatThreadTitle = (thread: ChatThread): string => {
    if (thread.title?.trim()) {
      return thread.title.length > 48 ? `${thread.title.slice(0, 48)}…` : thread.title;
    }
    return t('new_chat');
  };

  const historyPanel = (
    <aside
      className={cn(
        'flex h-full min-h-0 shrink-0 flex-col self-stretch overflow-hidden border-l border-border bg-muted/30 transition-[width] duration-200',
        historyOpen ? 'w-56' : 'w-9'
      )}
    >
      <div
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
        style={
          isEmbeddedInModal
            ? {
                paddingTop: 'var(--entity-detail-header-height, 4rem)',
                paddingBottom: 'var(--entity-detail-footer-height, 5rem)',
              }
            : undefined
        }
      >
        <div
          className={cn(
            'flex shrink-0 items-center gap-1 border-b border-border px-1 py-1.5',
            historyOpen ? 'justify-between pl-2' : 'justify-center'
          )}
        >
          {historyOpen ? (
            <span className="truncate text-xs font-medium text-muted-foreground">
              {t('previous_conversations')}
            </span>
          ) : null}
          <ButtonTooltip
            tooltip={
              historyOpen
                ? t('character_chat_history_hide_tooltip')
                : t('character_chat_history_show_tooltip')
            }
            side="left"
          >
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0"
              aria-expanded={historyOpen}
              aria-label={
                historyOpen
                  ? t('character_chat_history_hide_tooltip')
                  : t('character_chat_history_show_tooltip')
              }
              onClick={() => setHistoryOpen((open) => !open)}
            >
              {historyOpen ? (
                <ChevronRight className="h-4 w-4" aria-hidden />
              ) : (
                <ChevronLeft className="h-4 w-4" aria-hidden />
              )}
            </Button>
          </ButtonTooltip>
        </div>

        {historyOpen ? (
          <div className="flex min-h-0 flex-1 flex-col gap-2 p-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full justify-start gap-1.5"
              onClick={startNewChat}
            >
              <Plus className="h-3.5 w-3.5 shrink-0" aria-hidden />
              {t('new_chat')}
            </Button>
            <div className="min-h-0 flex-1 space-y-1 overflow-y-auto">
              {threads.length === 0 ? (
                <p className="px-1 text-xs text-muted-foreground">{t('no_threads_yet')}</p>
              ) : (
                threads.map((thread) => (
                  <button
                    key={thread.id}
                    type="button"
                    onClick={() => {
                      void loadMessages(thread.id);
                    }}
                    className={cn(
                      'w-full rounded-md border px-2 py-1.5 text-left text-xs transition-colors',
                      activeThreadId === thread.id
                        ? CHAT_ACTIVE_THREAD_CLASSNAME
                        : 'border-border bg-background text-foreground hover:bg-muted/80'
                    )}
                  >
                    <span className="line-clamp-2 break-words">{formatThreadTitle(thread)}</span>
                  </button>
                ))
              )}
              <div ref={threadListEndRef} />
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center px-1 py-1.5">
            <ButtonTooltip tooltip={t('character_chat_new_chat_tooltip')} side="left">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0"
                aria-label={t('new_chat')}
                onClick={startNewChat}
              >
                <Plus className="h-4 w-4" aria-hidden />
              </Button>
            </ButtonTooltip>
          </div>
        )}
      </div>
    </aside>
  );

  return (
    <div
      className={cn(
        'flex h-full min-h-0 min-w-0 flex-col overflow-hidden',
        !isEmbeddedInModal && 'min-h-[320px] rounded-md border border-border'
      )}
    >
      {CreditLimitModalComponent}
      <div className="flex min-h-0 min-w-0 flex-1 items-stretch overflow-hidden">
        {transcript}
        {historyPanel}
      </div>
      {isEmbeddedInModal
        ? renderedChrome
        : renderedChrome
          ? (
              <div className="border-t border-border px-3 py-3">{renderedChrome}</div>
            )
          : null}
    </div>
  );
};
